import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import AppLayout from '@/components/layout/AppLayout';
import { Envelope, Spinner, CheckCircle, Warning } from '@phosphor-icons/react';
import { InboxStatusStrip } from './inbox/InboxStatusStrip';
import { InboxHowItWorks } from './inbox/InboxHowItWorks';
import { PendingEmailCard } from './inbox/PendingEmailCard';
import { AssignedEmailRow } from './inbox/AssignedEmailRow';

export default function InboxPage() {
  const [emails, setEmails] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inboundDomain, setInboundDomain] = useState('');
  const [status, setStatus] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [discarding, setDiscarding] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, c, cfg, st] = await Promise.allSettled([
        api.get('/inbox/emails'),
        api.get('/cases'),
        api.get('/inbound/config'),
        api.get('/inbound/status'),
      ]);
      if (e.status === 'fulfilled') setEmails(e.value.data.emails || []);
      if (c.status === 'fulfilled') setCases(c.value.data.cases || []);
      if (cfg.status === 'fulfilled') setInboundDomain(cfg.value.data.inbound_domain || '');
      if (st.status === 'fulfilled') setStatus(st.value.data);
    } catch (err) {
      logger.error('Inbox load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assignTo = async (inboxId, caseId) => {
    if (!caseId) return;
    setAssigning(inboxId);
    try {
      await api.post(`/inbox/emails/${inboxId}/assign`, { case_id: caseId });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setAssigning(null);
    }
  };

  const discard = async (inboxId) => {
    if (!window.confirm('Discard this email? It will be hidden from the inbox.')) return;
    setDiscarding(inboxId);
    try {
      await api.delete(`/inbox/emails/${inboxId}`);
      await load();
    } catch (err) {
      alert('Failed to discard');
    } finally {
      setDiscarding(null);
    }
  };

  const pending = emails.filter(e => e.status === 'pending');
  const assigned = emails.filter(e => e.status === 'assigned');

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-[#002FA7]" /></div></AppLayout>;
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-6 sm:p-8" data-testid="inbox-page">
        <div className="mb-6">
          <h1 className="font-outfit text-3xl font-bold text-[#09090B] flex items-center gap-2">
            <Envelope size={28} className="text-[#002FA7]" /> Email Inbox
          </h1>
          <p className="text-sm text-[#6B7280] font-ibm mt-1">Forward emails to the per-case address printed inside each case, or send to a generic address and assign them here.</p>
        </div>

        <InboxStatusStrip status={status} />
        <InboxHowItWorks inboundDomain={inboundDomain} />

        {/* Pending */}
        <h2 className="font-outfit text-lg font-semibold text-[#09090B] mb-2 flex items-center gap-2">
          <Warning size={18} className="text-amber-600" /> Awaiting assignment ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <div className="text-center py-10 bg-white border border-dashed border-[#E5E7EB] rounded-lg mb-6">
            <Envelope size={36} className="mx-auto text-[#E5E7EB] mb-2" />
            <p className="text-sm font-ibm text-[#6B7280]">No emails awaiting assignment.</p>
          </div>
        ) : (
          <div className="space-y-2 mb-8">
            {pending.map(e => (
              <PendingEmailCard key={e._id} email={e} cases={cases} assigning={assigning} discarding={discarding} assignTo={assignTo} discard={discard} />
            ))}
          </div>
        )}

        {/* Assigned (recent) */}
        {assigned.length > 0 && (
          <>
            <h2 className="font-outfit text-lg font-semibold text-[#09090B] mb-2 flex items-center gap-2">
              <CheckCircle size={18} className="text-green-600" /> Assigned ({assigned.length})
            </h2>
            <div className="space-y-2">
              {assigned.slice(0, 20).map(e => (
                <AssignedEmailRow key={e._id} email={e} />
              ))}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
